import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  QueryList,
  ViewChildren,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  NgZone,
} from '@angular/core';
import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ColumnConfig, MasterSummaryConfig, PaginationConfig } from '../dynamic-table/column-config.model';

@Component({
  selector: 'app-master-detail-layout',
  templateUrl: './master-detail-layout.component.html',
  styleUrls: ['./master-detail-layout.component.scss'],
  standalone: false,
})
export class MasterDetailLayoutComponent implements OnInit, OnChanges, OnDestroy {

  /* ═══════════════════════════════════════════
     MASTER TABLE CONFIGURATION
     ═══════════════════════════════════════════ */

  /** Column definitions for the master (outer) table */
  @Input() masterColumns: ColumnConfig[] = [];

  /** Data array for the master table */
  @Input() masterData: any[] = [];

  /** Server-side pagination for master table. Null = client-side. */
  @Input() masterPagination: PaginationConfig | null = null;

  /** Enable sorting on master table. Default true */
  @Input() masterSortable: boolean = true;

  /** Field to uniquely identify master rows (e.g. 'orderNo', 'custName') */
  @Input() masterSelectionKey: string = '';

  /** Custom cell templates for master table */
  @Input() masterCellTemplates: { [key: string]: TemplateRef<any> } = {};

  /* ═══════════════════════════════════════════
     DETAIL TABLE CONFIGURATION
     ═══════════════════════════════════════════ */

  /** Column definitions for the detail (child) table */
  @Input() detailColumns: ColumnConfig[] = [];

  /** Data array for the detail table (loaded when a master row is selected) */
  @Input() detailData: any[] = [];

  /** Server-side pagination for detail table. Null = client-side. */
  @Input() detailPagination: PaginationConfig | null = null;

  /** Enable sorting on detail table. Default true */
  @Input() detailSortable: boolean = true;

  /** Custom cell templates for detail table */
  @Input() detailCellTemplates: { [key: string]: TemplateRef<any> } = {};

  /* ═══════════════════════════════════════════
     MASTER-DETAIL LAYOUT CONFIGURATION
     ═══════════════════════════════════════════ */

  /** Left panel summary configuration */
  @Input() summaryConfig!: MasterSummaryConfig;

  /** Left panel title text */
  @Input() leftPanelTitle: string = 'All Records';

  /** Detail panel header — shown above the detail table */
  @Input() detailHeaderField: string = '';

  /** Prefix for the detail header (e.g. 'PO#') */
  @Input() detailHeaderPrefix: string = '';

  /** Custom function for detail header text */
  @Input() detailHeaderFn: ((row: any) => string) | null = null;

  /** Left panel width (flex value). Default '20' */
  @Input() leftPanelFlex: string = '20';

  /** Right panel width (flex value). Default '80' */
  @Input() rightPanelFlex: string = '80';

  /** Whether to hide the left-panel paginator. Default false */
  @Input() hideLeftPaginator: boolean = false;

  /** If true, parent handles data slicing (server-side pagination). Default false (client-side). */
  @Input() masterServerSidePagination: boolean = false;

  /** If true, rows are not clickable and detail view is disabled. Default false. */
  @Input() disableRowClick: boolean = false;

  /** If true, only split view is shown (no master table, no close button). Default false. */
  @Input() showSplitViewOnly: boolean = false;

  /** Custom template to render in the right panel instead of the detail table. */
  @Input() detailContentTemplate: TemplateRef<any> | null = null;

  /** Loading state for detail data */
  @Input() detailLoading: boolean = false;

  /* ═══════════════════════════════════════════
     OUTPUTS
     ═══════════════════════════════════════════ */

  /** Emitted when a master row is clicked */
  @Output() masterRowClick = new EventEmitter<any>();

  /** Emitted when the close button is clicked in detail view */
  @Output() detailClose = new EventEmitter<void>();

  /** Emitted when master table page changes */
  @Output() masterPageChange = new EventEmitter<PageEvent>();

  /** Emitted when detail table page changes */
  @Output() detailPageChange = new EventEmitter<PageEvent>();

  /** Emitted when master sort changes */
  @Output() masterSortChange = new EventEmitter<Sort>();

  /** Emitted when detail sort changes */
  @Output() detailSortChange = new EventEmitter<Sort>();

  /* ═══════════════════════════════════════════
     INTERNAL STATE
     ═══════════════════════════════════════════ */

  showMasterDetail: boolean = false;
  selectedRow: any = null;
  summaryExpanded: boolean = true;

  ngOnInit(): void {
    if (this.showSplitViewOnly) {
      this.showMasterDetail = true;
      if (this.masterData?.length > 0) {
        this.onMasterRowClick(this.masterData[0]);
      }
    }
  }

  /* ── Panel resize state ── */
  private isResizing = false;
  private boundResizeMove: (e: MouseEvent) => void;
  private boundResizeEnd: () => void;
  private resizeOverlay: HTMLElement | null = null;

  @ViewChild('masterDetailContainer') masterDetailContainer!: ElementRef;
  @ViewChild('listContainer') listContainer!: ElementRef;
  @ViewChildren('listItem') listItems!: QueryList<ElementRef>;

  constructor(private ngZone: NgZone) {
    this.boundResizeMove = this.onPanelResizeMove.bind(this);
    this.boundResizeEnd = this.onPanelResizeEnd.bind(this);
  }

  /** Returns the current page's slice of masterData for the left panel list */
  get paginatedMasterData(): any[] {
    if (!this.masterPagination || !this.masterData) return this.masterData || [];
    // When parent handles pagination (server-side), data is already the current page
    if (this.masterServerSidePagination) return this.masterData;
    const start = this.masterPagination.pageIndex * this.masterPagination.pageSize;
    return this.masterData.slice(start, start + this.masterPagination.pageSize);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // If master data changes and we were in detail view, auto-select first row
    if (changes['masterData'] && this.showMasterDetail && this.masterData?.length > 0) {
      // Re-select same row if available, otherwise first
      if (this.selectedRow && this.masterSelectionKey) {
        const match = this.masterData.find(
          r => r[this.masterSelectionKey] === this.selectedRow[this.masterSelectionKey]
        );
        if (match) {
          this.onMasterRowClick(match);
          return;
        }
      }
      this.onMasterRowClick(this.masterData[0]);
    }

    // If master data is empty and we're in detail view, close detail or clear selection
    if (changes['masterData'] && this.showMasterDetail && (!this.masterData || this.masterData.length === 0)) {
      if (this.showSplitViewOnly) {
        this.selectedRow = null;
      } else {
        this.closeDetail();
      }
    }
  }

  /* ═══════════════════════════════════════════
     ACTIONS
     ═══════════════════════════════════════════ */

  onMasterRowClick(row: any): void {
    // If clicking the already-selected row, toggle expand/collapse
    if (this.selectedRow && this.masterSelectionKey &&
        row[this.masterSelectionKey] === this.selectedRow[this.masterSelectionKey]) {
      this.summaryExpanded = !this.summaryExpanded;
      return;
    }
    this.selectedRow = row;
    this.summaryExpanded = true;
    this.showMasterDetail = true;
    this.masterRowClick.emit(row);

    // Scroll to selected in left panel
    setTimeout(() => this.scrollToSelected(row), 100);
  }

  closeDetail(): void {
    if (this.showSplitViewOnly) return;
    this.selectedRow = null;
    this.showMasterDetail = false;
    this.detailClose.emit();
  }

  onMasterPageChange(event: PageEvent): void {
    this.masterPageChange.emit(event);
  }

  onDetailPageChange(event: PageEvent): void {
    this.detailPageChange.emit(event);
  }

  onMasterSortChange(sort: Sort): void {
    this.masterSortChange.emit(sort);
  }

  onDetailSortChange(sort: Sort): void {
    this.detailSortChange.emit(sort);
  }

  /* ── Panel resize handlers ── */

  onPanelResizeStart(event: MouseEvent): void {
    event.preventDefault();
    this.isResizing = true;
    this.createResizeOverlay();
    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('mousemove', this.boundResizeMove);
      document.addEventListener('mouseup', this.boundResizeEnd);
    });
  }

  private onPanelResizeMove(event: MouseEvent): void {
    if (!this.isResizing || !this.masterDetailContainer) return;
    const containerEl: HTMLElement = this.masterDetailContainer.nativeElement;
    const containerRect = containerEl.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const offsetX = event.clientX - containerRect.left;
    const percent = Math.min(50, Math.max(10, (offsetX / containerWidth) * 100));
    this.ngZone.run(() => {
      this.leftPanelFlex = String(Math.round(percent));
      this.rightPanelFlex = String(100 - Math.round(percent));
    });
  }

  private onPanelResizeEnd(): void {
    this.isResizing = false;
    this.removeResizeOverlay();
    document.removeEventListener('mousemove', this.boundResizeMove);
    document.removeEventListener('mouseup', this.boundResizeEnd);
  }

  private createResizeOverlay(): void {
    this.resizeOverlay = document.createElement('div');
    this.resizeOverlay.style.position = 'fixed';
    this.resizeOverlay.style.top = '0';
    this.resizeOverlay.style.left = '0';
    this.resizeOverlay.style.width = '100%';
    this.resizeOverlay.style.height = '100%';
    this.resizeOverlay.style.cursor = 'col-resize';
    this.resizeOverlay.style.zIndex = '9999';
    document.body.appendChild(this.resizeOverlay);
  }

  private removeResizeOverlay(): void {
    if (this.resizeOverlay) {
      this.resizeOverlay.remove();
      this.resizeOverlay = null;
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('mousemove', this.boundResizeMove);
    document.removeEventListener('mouseup', this.boundResizeEnd);
  }

  /* ═══════════════════════════════════════════
     HELPERS
     ═══════════════════════════════════════════ */

  getDetailHeader(): string {
    if (!this.selectedRow) { return ''; }
    if (this.detailHeaderFn) { return this.detailHeaderFn(this.selectedRow); }
    const value = this.selectedRow[this.detailHeaderField] || '';
    return this.detailHeaderPrefix ? `${this.detailHeaderPrefix} ${value}` : String(value);
  }

  getPrimaryText(row: any): string {
    if (!this.summaryConfig) { return ''; }
    if (this.summaryConfig.primaryDisplayFn) {
      return this.summaryConfig.primaryDisplayFn(row);
    }
    const value = row[this.summaryConfig.primaryField] || '';
    return this.summaryConfig.primaryPrefix
      ? `${this.summaryConfig.primaryPrefix} ${value}`
      : String(value);
  }

  getSecondaryText(row: any): string {
    if (!this.summaryConfig) { return ''; }
    if (this.summaryConfig.secondaryDisplayFn) {
      return this.summaryConfig.secondaryDisplayFn(row);
    }
    if (!this.summaryConfig.secondaryField) { return ''; }
    const value = row[this.summaryConfig.secondaryField] || '';
    return this.summaryConfig.secondaryPrefix
      ? `${this.summaryConfig.secondaryPrefix} ${value}`
      : String(value);
  }

  isRowSelected(row: any): boolean {
    if (!this.selectedRow || !this.masterSelectionKey) { return false; }
    return row[this.masterSelectionKey] === this.selectedRow[this.masterSelectionKey];
  }

  private scrollToSelected(row: any): void {
    if (!this.listItems || !this.listContainer) { return; }

    const displayedData = this.showMasterDetail ? this.paginatedMasterData : this.masterData;
    const index = displayedData.findIndex(
      r => this.masterSelectionKey
        ? r[this.masterSelectionKey] === row[this.masterSelectionKey]
        : r === row
    );
    if (index === -1) { return; }

    const itemArray = this.listItems.toArray();
    if (itemArray[index]) {
      const el = itemArray[index].nativeElement;
      const container = this.listContainer.nativeElement;
      const scrollPos = el.offsetTop - (container.clientHeight / 2) + (el.offsetHeight / 2);
      container.scrollTo({ top: scrollPos, behavior: 'smooth' });
    }
  }

  /* ═══════════════════════════════════════════
     SUMMARY FIELD HELPERS
     ═══════════════════════════════════════════ */

  private currencyPipe = new CurrencyPipe('en-US');
  private decimalPipe = new DecimalPipe('en-US');
  private datePipe = new DatePipe('en-US');

  formatSummaryValue(row: any, field: { label: string; field: string; type?: string; displayMode?: 'default' | 'split-text-amount'; rightHeader?: string; currencyFormat?: string; numberFormat?: string; dateFormat?: string }): string {
    const raw = row[field.field];
    if (raw == null || raw === '') return '-';
    switch (field.type) {
      case 'currency':
        return this.currencyPipe.transform(raw, 'USD', 'symbol', field.currencyFormat || '1.2-2') || String(raw);
      case 'number':
        return this.decimalPipe.transform(raw, field.numberFormat || '1.0-0') || String(raw);
      case 'date':
        return this.datePipe.transform(raw, field.dateFormat || 'MM/dd/yyyy') || String(raw);
      default:
        return String(raw);
    }
  }

  getSplitSummaryLeftBlock(
    row: any,
    field: { label: string; field: string; type?: string; displayMode?: 'default' | 'split-text-amount'; rightHeader?: string; currencyFormat?: string; numberFormat?: string; dateFormat?: string }
  ): string {
    const raw = row?.[field.field];
    if (raw == null || raw === '') {
      return field.label || '';
    }

    const lines = String(raw)
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    const rightHeader = field.rightHeader || 'Amount';
    const leftLines = lines
      .filter(line => !new RegExp(`^${rightHeader}\\s*$`, 'i').test(line))
      .map(line => line.replace(/\s+(-?\$?\d[\d,]*\.?\d*)\s*$/, '').trim())
      .filter(line => line.length > 0);

    return [field.label || '', ...leftLines].filter(line => line.length > 0).join('\n');
  }

  getSplitSummaryRightBlock(
    row: any,
    field: { label: string; field: string; type?: string; displayMode?: 'default' | 'split-text-amount'; rightHeader?: string; currencyFormat?: string; numberFormat?: string; dateFormat?: string }
  ): string {
    const raw = row?.[field.field];
    const rightHeader = field.rightHeader || 'Amount';

    if (raw == null || raw === '') {
      return rightHeader;
    }

    const lines = String(raw)
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);

    const amounts = lines
      .map(line => {
        const match = line.match(/(-?\$?\d[\d,]*\.?\d*)\s*$/);
        return match ? match[1].replace('$', '') : '';
      })
      .filter(line => line.length > 0);

    return [rightHeader, ...amounts].join('\n');
  }
}
